import type { RuntimeComponentDownloadStatus } from '../shared/desktop-api';
import { engagementChannel } from './engagement-channel';
import { serverEngagement } from './server-engagement';

/** Nothing to undo: nothing here ever changes. */
const unsubscribe = (): (() => void) => () => undefined;

/** Nothing to download: Paige's readers are pools, so the status bar's download strip never shows. */
const nothingToDownload: RuntimeComponentDownloadStatus = {
  state: 'complete',
  receivedBytes: 0,
  totalBytes: 0,
  fraction: 1,
  remaining: 0,
};

/**
 * The desktop's own bookkeeping, answered on purpose with "none" (2026-10-04). Each of these is asked
 * on every workbench load; resolved by the adapter's stand-in they warned in the console with a stack
 * trace in production, for things that are not missing but meaningless in a browser:
 *
 * - an earlier CapturePoint on this computer (`detectLegacyInstall`): never, there is no computer;
 * - reader runtimes downloaded on first use (`getRuntimeComponentStatus` and its subscription):
 *   nothing to download, already complete;
 * - the desktop's engagement record (`getEngagement`, `onEngagementChanged`,
 *   `noteOnboardingMilestone`, `adoptOnboardingMilestones`, `setEngagementConsent`): not
 *   dropped but moved to the server, per person per company (server-engagement.ts,
 *   docs/engineering/engagement.md); the tour's offer, the documents list's hints and the tips
 *   email read it;
 * - the desktop's usage telemetry (`trackTelemetry`): dropped. Paige has its own stream
 *   (docs/runbooks/product-analytics.md) and already records what this would duplicate: screens
 *   from the router, uncaught errors and refused requests as friction, and the workbench's named
 *   flows through `trackProduct`. What remains is a click by every control's label, every shortcut
 *   and the text of every alert, which that stream's rule (a name and a count, never a value) does
 *   not admit;
 * - CapturePoint's email asks (CPv6 af3d79c4, 7b1a13da) are not here: the learning note and the shortcut
 *   sheet are Paige's own mail, answered by its API (server-email-asks.ts, 2026-10-20); the speed report
 *   stays the desktop's, since its speed test is hidden in Paige (`local-performance`);
 * - `capturepoint://` links from a web page or email (`onAppLink`, CPv6 639c2452) are Paige's own
 *   addresses (app-links.ts);
 * - CapturePoint's support channel driving the open window (`onJobsChanged`, `onSupportWindowCommand`,
 *   `replySupportWindowCommand`; CPv6 9a1d1a8b, its ADR-0088): the desktop's local support switch, which
 *   Paige hides (`support-access`). Paige's support never drives a person's screen: staff work in the
 *   company through their own grant (docs/runbooks/incidents.md, "Staff access to a customer"), and the
 *   screen reads what changed from the API as it does after any colleague's change. So no event ever
 *   arrives, and a reply, which only answers an event, has nowhere to go and says so.
 */
export function desktopOnlyAnswers(
  api: string,
  tenant: string | undefined,
): Record<string, unknown> {
  return {
    detectLegacyInstall: async () => false,
    getRuntimeComponentStatus: async () => nothingToDownload,
    onRuntimeComponentStatusChanged: unsubscribe,
    ...serverEngagement(engagementChannel(api, tenant)),
    trackTelemetry: () => undefined,
    onJobsChanged: unsubscribe,
    onSupportWindowCommand: unsubscribe,
    replySupportWindowCommand: async () => {
      throw new Error('Paige has no support channel into this window.');
    },
  };
}
